import type { CostRow, Label, LabelStatus, Offer, Overview, Package, ProviderId, Quote, RoutingPolicy, Tracking, Usage } from '@trivio/contracts';

/**
 * Thin client for the Trivio API (TTP-243). Two auth shapes coexist here,
 * matching the backend exactly (see apps/api/src/app.ts + the modules under
 * apps/api/src/modules/{tenancy,auth,providers}):
 *
 *  - `/internal/*` (orgs, api-keys) — Firebase Auth ID token
 *    (`Authorization: Bearer <idToken>`), dashboard-only, never part of the
 *    public `/v1` OpenAPI contract.
 *  - `/v1/*` (providers, quotes, ...) — a Trivio API key
 *    (`Authorization: Bearer trv_live_...` / `trv_test_...`). Which key
 *    (`live` vs `test`) is passed determines `request.mode` on the backend,
 *    which in turn decides live vs. sandbox behavior everywhere, including
 *    `PUT /providers/{provider}/credentials` — see
 *    src/features/onboarding/ApiKeysContext.tsx for why the onboarding flow
 *    is careful about which of the two it uses at each step.
 *
 * `Org`/`CreatedApiKey`/`ApiKeyMetadata` are declared locally (not imported
 * from @trivio/contracts) because `/internal` routes are intentionally
 * outside the public YAML contract by design (see
 * apps/api/src/modules/tenancy/orgs.routes.ts's docstring) — mirrors the
 * real handler shapes, confirmed by reading
 * apps/api/src/modules/tenancy/orgs.routes.ts,
 * apps/api/src/modules/auth/api-keys.routes.ts,
 * apps/api/src/modules/auth/api-keys.service.ts and
 * apps/api/src/modules/auth/api-keys.repository.ts directly. Likewise
 * `ProviderConnection` mirrors apps/api/src/modules/providers/
 * connections.repository.ts's `ProviderConnection` — it matches the YAML's
 * `ProviderConnection` schema 1:1 but isn't itself exported from
 * @trivio/contracts (only hand-mirrored YAML schemas live there today).
 */

// Defaults to '' (relative, same-origin) rather than an absolute
// 'http://localhost:8080' guess — `vite dev`'s proxy (vite.config.ts)
// forwards /internal and /v1 to the local API on that same-origin path,
// which sidesteps CORS entirely (apps/api doesn't have it configured as of
// TTP-243 — that's the separate, parallel TTP-260). A production build
// without VITE_API_BASE_URL set falls back to the SAME relative behavior,
// which is wrong for a real deployment (the API lives on a different
// origin than the dashboard's Firebase Hosting domain) — but there's no
// correct absolute URL to guess yet either (see apps/dashboard/.env.example),
// and failing loudly (404s against the hosting domain) beats silently
// guessing a wrong one.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Absolute API origin for human-facing display only (Tela 5's `curl`
 * snippet, ApiKeyStep.tsx) — a relative `API_BASE_URL` (the fetch default
 * above) would render a copy-pasteable-looking but broken
 * `curl 'https://…/v1/quotes'`-less command outside the browser (no host).
 * Falls back to the documented local API port (apps/api/package.json's
 * `dev` script) when `VITE_API_BASE_URL` isn't set, same default this repo
 * uses everywhere else for "the local API".
 */
export const API_DISPLAY_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';

// ---------------------------------------------------------------------------
// RFC 9457 (application/problem+json) error shape — mirrors
// apps/api/src/plugins/problem-json.ts's ProblemDetails exactly.
// ---------------------------------------------------------------------------

export interface ProblemFieldError {
  field: string;
  message: string;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: ProblemFieldError[];
}

/** Thrown for every non-2xx response — carries the parsed RFC 9457 body when available. */
export class ApiProblemError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiProblemError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** Convenience: the field-level message for a given form field, if the API reported one. */
  fieldError(field: string): string | undefined {
    return this.problem.errors?.find((e) => e.field === field)?.message;
  }
}

async function parseProblem(response: Response): Promise<ProblemDetails> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'title' in body) {
      return body as ProblemDetails;
    }
  } catch {
    // Response body wasn't JSON (e.g. a network/proxy error page) — fall through to the generic problem below.
  }
  return {
    type: 'about:blank',
    title: response.statusText || 'Error',
    status: response.status,
    detail: `Request failed with status ${response.status}.`,
  };
}

interface RequestOptions {
  method?: string;
  token?: string;
  body?: unknown;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.token) {
    headers['Authorization'] = `Bearer ${options.token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // Spread instead of `body: ... ? ... : undefined` — exactOptionalPropertyTypes
      // rejects assigning `undefined` to RequestInit['body'] explicitly, even
      // though the property itself is optional.
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    });
  } catch {
    throw new ApiProblemError({
      type: 'about:blank',
      title: 'Falha de rede',
      status: 0,
      detail: 'Não foi possível conectar à API do Trivio. Verifique sua conexão e tente novamente.',
    });
  }

  if (!response.ok) {
    throw new ApiProblemError(await parseProblem(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// /internal/orgs — Firebase ID token auth.
// ---------------------------------------------------------------------------

/** Mirrors OrgsRepository#createOrg's return shape (apps/api/src/modules/tenancy/orgs.repository.ts). */
export interface Org {
  id: string;
  cnpj: string;
  legal_name: string;
  trade_name?: string;
}

export function createOrg(
  idToken: string,
  body: { cnpj: string; legal_name: string; trade_name?: string },
): Promise<Org> {
  return request<Org>('/internal/orgs', { method: 'POST', token: idToken, body });
}

export function selectOrg(idToken: string, orgId: string): Promise<{ orgId: string }> {
  return request<{ orgId: string }>(`/internal/orgs/${encodeURIComponent(orgId)}/select`, {
    method: 'POST',
    token: idToken,
  });
}

/**
 * Mirrors `OrgsRepository#listOrgsForUser`'s `OrgMembership`
 * (apps/api/src/modules/tenancy/orgs.repository.ts) — deliberately narrower
 * than the backend type: it OMITS `inbound_webhook_secret`. That field is
 * internal plumbing for the inbound tracking webhook (see the repository's
 * docstring) and must never reach the dashboard UI (TTP-244 brief) — leaving
 * it off this type means a future accidental `{...membership}` render can't
 * leak it through the type system, even though the raw JSON response still
 * technically carries it over the wire.
 */
export interface OrgMembership {
  id: string;
  legal_name: string;
  trade_name?: string;
  role: 'owner' | 'member';
  joined_at: string;
  /**
   * SERVER-side "this org finished onboarding" signal (TTP-266 incident
   * fix) — `null` until `completeOnboarding` below has been called once for
   * this org. This is the source of truth `guards.tsx` checks BEFORE
   * falling back to the (client-only, wipeable) `localStorage` progress —
   * see that file's docstring for the incident this closes (onboarding
   * repeating, and the onboarding API keys being revoked/recreated, on
   * every fresh browser/device for an org that had already finished).
   */
  onboarding_completed_at: string | null;
}

/** Powers the org switcher (P3 — "alternar entre organizações com o mesmo login", TTP-244) AND `guards.tsx`'s server-side onboarding-completion check (TTP-266). */
export async function listOrgs(idToken: string): Promise<OrgMembership[]> {
  const { orgs } = await request<{ orgs: OrgMembership[] }>('/internal/orgs', { token: idToken });
  return orgs;
}

/**
 * `POST /internal/orgs/{orgId}/complete-onboarding` (TTP-266) — called once,
 * from `ApiKeyStep.tsx#handleFinish`, right before navigating to the
 * dashboard. Idempotent server-side (a retry after a network error here is
 * always safe).
 */
export function completeOnboarding(idToken: string, orgId: string): Promise<void> {
  return request<void>(`/internal/orgs/${encodeURIComponent(orgId)}/complete-onboarding`, {
    method: 'POST',
    token: idToken,
  });
}

// ---------------------------------------------------------------------------
// /internal/api-keys — Firebase ID token auth.
// ---------------------------------------------------------------------------

export type ApiKeyMode = 'live' | 'test';

/** Mirrors ApiKeysRepository's ApiKeyMetadata (apps/api/src/modules/auth/api-keys.repository.ts) — never carries the raw key. */
export interface ApiKeyMetadata {
  id: string;
  prefix: string;
  mode: ApiKeyMode;
  name: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** Mirrors ApiKeysService#create's CreatedApiKey — `key` (plaintext) is present ONLY in this response, never again. */
export interface CreatedApiKey extends ApiKeyMetadata {
  key: string;
}

export function createApiKey(idToken: string, name: string, mode: ApiKeyMode): Promise<CreatedApiKey> {
  return request<CreatedApiKey>('/internal/api-keys', { method: 'POST', token: idToken, body: { name, mode } });
}

export async function listApiKeys(idToken: string): Promise<ApiKeyMetadata[]> {
  const { api_keys: apiKeys } = await request<{ api_keys: ApiKeyMetadata[] }>('/internal/api-keys', {
    token: idToken,
  });
  return apiKeys;
}

export function revokeApiKey(idToken: string, keyId: string): Promise<void> {
  return request<void>(`/internal/api-keys/${encodeURIComponent(keyId)}`, { method: 'DELETE', token: idToken });
}

/**
 * `POST /internal/api-keys/{keyId}/reactivate` (TTP-266) — un-revokes a
 * previously-revoked key, preserving its original hash/prefix (NOT
 * rotation — the lojista's already-configured secret keeps working).
 * `422` if reactivating would exceed the 10-active-keys-per-mode cap, `404`
 * if the key doesn't exist in the caller's org.
 */
export function reactivateApiKey(idToken: string, keyId: string): Promise<ApiKeyMetadata> {
  return request<ApiKeyMetadata>(`/internal/api-keys/${encodeURIComponent(keyId)}/reactivate`, {
    method: 'POST',
    token: idToken,
  });
}

/**
 * `DELETE /internal/api-keys/{keyId}/permanently` (TTP-266) — irreversible
 * hard delete. `409` if the key is still active (must be revoked first);
 * distinct from `revokeApiKey` above (soft, reversible via
 * `reactivateApiKey`).
 */
export function deleteApiKeyPermanently(idToken: string, keyId: string): Promise<void> {
  return request<void>(`/internal/api-keys/${encodeURIComponent(keyId)}/permanently`, {
    method: 'DELETE',
    token: idToken,
  });
}

// ---------------------------------------------------------------------------
// /v1/providers — Trivio API key auth (trv_live_/trv_test_).
// ---------------------------------------------------------------------------

export type ProviderConnectionStatus = 'active' | 'invalid_credentials' | 'disconnected';

/** Mirrors apps/api/src/modules/providers/connections.repository.ts's ProviderConnection (== the YAML's ProviderConnection schema). */
export interface ProviderConnection {
  provider: ProviderId;
  status: ProviderConnectionStatus;
  connected_at: string;
  last_validated_at: string;
}

/** Best-effort prefill for Tela 3 on a revisit/resume — never required for the happy path to work. */
export function listProviders(apiKey: string): Promise<ProviderConnection[]> {
  return request<ProviderConnection[]>('/v1/providers', { token: apiKey });
}

export function putProviderCredentials(
  apiKey: string,
  provider: ProviderId,
  providerApiKey: string,
): Promise<ProviderConnection> {
  return request<ProviderConnection>(`/v1/providers/${provider}/credentials`, {
    method: 'PUT',
    token: apiKey,
    body: { api_key: providerApiKey },
  });
}

/** `DELETE /v1/providers/{provider}/credentials` (TTP-244 `/providers` "desconectar") — 204, no body. */
export function deleteProviderCredentials(apiKey: string, provider: ProviderId): Promise<void> {
  return request<void>(`/v1/providers/${provider}/credentials`, { method: 'DELETE', token: apiKey });
}

/** Mirrors the YAML's `ProviderBalance` schema (never `raw` — that field only exists on the adapter-level type server-side). `null` in either field means the provider doesn't expose that concept — render as "não disponível", never as `0` (TTP-267). */
export interface ProviderBalance {
  balance_cents: number | null;
  label_limit: number | null;
}

/** `GET /v1/providers/{provider}/balance` (TTP-267) — no cache, always a fresh read (see ProvidersPage.tsx). `trv_test_` sessions get the adapter's deterministic sandbox fixture, never a real provider call. */
export function getProviderBalance(apiKey: string, provider: ProviderId): Promise<ProviderBalance> {
  return request<ProviderBalance>(`/v1/providers/${provider}/balance`, { token: apiKey });
}

// ---------------------------------------------------------------------------
// /v1/quotes — Trivio API key auth (trv_live_/trv_test_).
// ---------------------------------------------------------------------------

export interface QuoteRequestOptions {
  providers?: ProviderId[];
  routing_policy_override?: 'cheapest' | 'fastest' | 'balanced';
  external_ref?: string;
}

export interface QuoteRequestBody {
  from_postal_code: string;
  to_postal_code: string;
  packages: Package[];
  options?: QuoteRequestOptions;
}

export function postQuote(apiKey: string, body: QuoteRequestBody): Promise<Quote> {
  return request<Quote>('/v1/quotes', { method: 'POST', token: apiKey, body });
}

export type { Offer, Quote };

// ---------------------------------------------------------------------------
// /v1/labels + /v1/shipments/{id}/tracking — Trivio API key auth
// (TTP-244: always the dashboard session key, see
// src/app/DashboardSessionKeyContext.tsx — never the key(s) revealed once at
// onboarding's Tela 5). `Label`/`LabelStatus`/`Tracking` come straight from
// @trivio/contracts, not redeclared here.
// ---------------------------------------------------------------------------

export interface LabelListParams {
  status?: LabelStatus;
  external_ref?: string;
  cursor?: string;
  /** @default 50, per the API's own default (LABEL_LIST_QUERY_SCHEMA). */
  limit?: number;
}

/** Mirrors `LABEL_LIST_RESPONSE_SCHEMA` (apps/api/src/modules/labels/labels.schemas.ts). */
export interface LabelListResult {
  data: Label[];
  next_cursor: string | null;
}

export function listLabels(apiKey: string, params: LabelListParams = {}): Promise<LabelListResult> {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.external_ref) query.set('external_ref', params.external_ref);
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return request<LabelListResult>(`/v1/labels${qs ? `?${qs}` : ''}`, { token: apiKey });
}

export function getLabel(apiKey: string, labelId: string): Promise<Label> {
  return request<Label>(`/v1/labels/${encodeURIComponent(labelId)}`, { token: apiKey });
}

/** `POST /v1/labels/{id}/cancel` — a `409` (already terminal / can't cancel) surfaces as `ApiProblemError` with `problem.detail` set, same as every other endpoint here. */
export function cancelLabel(apiKey: string, labelId: string): Promise<Label> {
  return request<Label>(`/v1/labels/${encodeURIComponent(labelId)}/cancel`, { method: 'POST', token: apiKey });
}

export type LabelDocumentFormat = 'pdf' | 'zpl';

export interface LabelDocument {
  blob: Blob;
  contentType: string;
}

/**
 * `GET /v1/labels/{id}/document` — deliberately NOT built on the shared
 * `request<T>()` helper above: the response body is binary (PDF) or plain
 * text (ZPL), never JSON, so `response.json()` would throw on a successful
 * 200. Mirrors `request`'s error handling (network failure + non-2xx RFC
 * 9457 parsing) by hand instead.
 */
export async function getLabelDocument(
  apiKey: string,
  labelId: string,
  format: LabelDocumentFormat = 'pdf',
): Promise<LabelDocument> {
  const url = `${API_BASE_URL}/v1/labels/${encodeURIComponent(labelId)}/document?format=${format}`;

  let response: Response;
  try {
    response = await fetch(url, { headers: { Authorization: `Bearer ${apiKey}` } });
  } catch {
    throw new ApiProblemError({
      type: 'about:blank',
      title: 'Falha de rede',
      status: 0,
      detail: 'Não foi possível conectar à API do Trivio. Verifique sua conexão e tente novamente.',
    });
  }

  if (!response.ok) {
    throw new ApiProblemError(await parseProblem(response));
  }

  const blob = await response.blob();
  return { blob, contentType: response.headers.get('Content-Type') ?? 'application/octet-stream' };
}

/** `GET /v1/shipments/{labelId}/tracking` — note the path: `/shipments/...`, not `/labels/.../tracking` (see apps/api/src/modules/tracking/tracking.routes.ts). */
export function getTracking(apiKey: string, labelId: string): Promise<Tracking> {
  return request<Tracking>(`/v1/shipments/${encodeURIComponent(labelId)}/tracking`, { token: apiKey });
}

export type { Label, LabelStatus, Tracking };

// ---------------------------------------------------------------------------
// /v1/routing-policy — Trivio API key auth.
// ---------------------------------------------------------------------------

export function getRoutingPolicy(apiKey: string): Promise<RoutingPolicy> {
  return request<RoutingPolicy>('/v1/routing-policy', { token: apiKey });
}

/** Sends the FULL policy — the contract replaces it wholesale, there's no partial PATCH (TTP-244 notas técnicas). */
export function putRoutingPolicy(apiKey: string, policy: RoutingPolicy): Promise<RoutingPolicy> {
  return request<RoutingPolicy>('/v1/routing-policy', { method: 'PUT', token: apiKey, body: policy });
}

export type { RoutingPolicy };

// ---------------------------------------------------------------------------
// /v1/usage — Trivio API key auth (TTP-245's leftover dashboard piece).
// ---------------------------------------------------------------------------

/** `GET /v1/usage` — mirrors `apps/api/src/modules/metering/metering.routes.ts`; `tier_limit_*` are `null` while no tier/pricing model exists yet (TRV-20, Fase 2). */
export function getUsage(apiKey: string): Promise<Usage> {
  return request<Usage>('/v1/usage', { token: apiKey });
}

export type { Usage };

// ---------------------------------------------------------------------------
// /v1/costs — Trivio API key auth (TTP-250 / TRV-19).
// ---------------------------------------------------------------------------

export type CostGroupBy = 'provider' | 'carrier';

export interface CostListParams {
  /** Required, `YYYY-MM` — same string the API expects verbatim on the wire. */
  period: string;
  group_by?: CostGroupBy;
}

/** `GET /v1/costs` — no pagination (see `CostRow`'s docstring in @trivio/contracts): always the full array for the tenant/month. */
export function getCosts(apiKey: string, params: CostListParams): Promise<CostRow[]> {
  const query = new URLSearchParams({ period: params.period });
  if (params.group_by) query.set('group_by', params.group_by);
  return request<CostRow[]>(`/v1/costs?${query.toString()}`, { token: apiKey });
}

export type { CostRow };

// ---------------------------------------------------------------------------
// /v1/overview — Trivio API key auth (TTP-282).
// ---------------------------------------------------------------------------

/** `GET /v1/overview` — the `/` home screen's single aggregate call (usage + costs + recent labels + providers + routing policy for the current calendar month). No params: the period is always the current UTC month, same convention `/usage`/`/costs` already use. */
export function getOverview(apiKey: string): Promise<Overview> {
  return request<Overview>('/v1/overview', { token: apiKey });
}

export type { Overview };

// ---------------------------------------------------------------------------
// /internal/providers/frenet/deposit — Firebase ID token auth (TTP-270).
// Frenet-only ("Adicionar saldo" via Mercado Pago) — mirrors
// apps/api/src/modules/providers/frenet-deposit.routes.ts exactly. Not part
// of the public /v1 OpenAPI contract (same posture as every other
// /internal/* function in this file) — this is a dashboard-only action, the
// Mercado Pago checkout it opens next needs a real browser.
// ---------------------------------------------------------------------------

export type FrenetDepositStatus = 'pending' | 'succeeded' | 'failed';

/** Mirrors `apps/api/src/modules/providers/frenet-deposit.service.ts`'s `PublicFrenetDeposit` — never `raw`/`deposit_reference` (internal bookkeeping, not the dashboard's concern). */
export interface FrenetDeposit {
  deposit_id: string;
  status: FrenetDepositStatus;
  value_cents: number;
  created_at: string;
  updated_at: string;
}

export interface StartFrenetDepositResult {
  preference_id: string;
}

/**
 * `POST /internal/providers/frenet/deposit` (TTP-270) — starts a Frenet
 * wallet deposit; `value_cents` must be a positive integer (`422` from the
 * API otherwise, same convention every other endpoint uses for a schema
 * validation failure) and `404` when Frenet isn't connected for this org.
 * The caller (`useFrenetDeposit.ts`) uses `preference_id` to open Mercado
 * Pago's checkout next.
 */
export function createFrenetDeposit(idToken: string, valueCents: number): Promise<StartFrenetDepositResult> {
  return request<StartFrenetDepositResult>('/internal/providers/frenet/deposit', {
    method: 'POST',
    token: idToken,
    body: { value_cents: valueCents },
  });
}

/** `GET /internal/providers/frenet/deposit/latest` (TTP-270) — `deposit: null` when the org never attempted a Frenet deposit (a normal state, not an error). */
export function getLatestFrenetDeposit(idToken: string): Promise<{ deposit: FrenetDeposit | null }> {
  return request<{ deposit: FrenetDeposit | null }>('/internal/providers/frenet/deposit/latest', { token: idToken });
}
